<template>
  <div class="checklist">
    <div class="header">
      <div class="left">
        <CheckListSvg />
        Checklist
      </div>

      <q-btn
        :disable="!!editTask.id"
        :ripple="false"
        size="sm"
        class="q-pa-sm"
        flat
        dense
        color="primary"
        icon="fas fa-plus"
        @click="showNewTask = true"
      />
    </div>

    <q-separator />

    <div class="tasks">
      <template v-if="tasks">
        <div v-for="(task, index) of tasks" :key="task.id" :class="{ editable: task.id === editTask.id }" class="task">
          <CheckListInput
            v-if="task.id === editTask.id"
            ref="editTask"
            v-model="editTask.task.body"
            save-btn-text="Update"
            @cancel="clearEditTask"
            @save="onUpdate"
          />

          <template v-else>
            <div class="title">{{ index + 1 }}. {{ task.body }}</div>

            <div class="actions">
              <q-checkbox :value="task.completed" color="tertiary" dense @input="toggleCompletion(task)" />

              <q-icon
                name="fas fa-pencil"
                color="tertiary"
                class="cursor-pointer q-mr-sm q-ml-sm"
                @click="editClicked(task)"
              />

              <q-icon name="fas fa-trash" color="faded" class="cursor-pointer" @click="onDelete(task)" />
            </div>
          </template>
        </div>

        <CheckListInput
          v-if="showNewTask"
          ref="newTask"
          v-model="newTask"
          @cancel="showNewTask = false"
          @save="onSave"
        />

        <span v-if="!tasks.length && !showNewTask" class="no-tasks">No tasks</span>
      </template>

      <div v-else class="spinner-loader">
        <q-spinner color="primary" size="1.5em" />
      </div>
    </div>
  </div>
</template>

<script>
import * as apiTo from '../api'
import { cloneDeep } from 'lodash'
import { mapState } from 'vuex'
// Components
import CheckListInput from './CheckListInput'
import CheckListSvg from '@/reusable/svg-icons/CheckListSvg'

export default {
  components: {
    CheckListInput,
    CheckListSvg
  },

  props: {
    editItem: Object,
    checklists: Array,
    fromPage: String
  },

  data: () => ({
    newTask: '',
    showNewTask: false,
    tasks: null,
    editTask: {
      id: null,
      task: null
    }
  }),

  computed: {
    ...mapState('todo-modal', ['selectedTemplate'])
  },

  watch: {
    showNewTask(val) {
      if (val) this.$nextTick(() => this.$refs.newTask.focus())
    },

    checklists: {
      handler(val) {
        if (this.editItem) this.tasks = cloneDeep(val)
        else this.tasks = []
      },
      immediate: true
    },

    selectedTemplate(template) {
      this.tasks = cloneDeep(template.checklist_items)
    }
  },

  methods: {
    editClicked(task) {
      this.editTask = {
        id: task.id,
        task: cloneDeep(task)
      }
      this.$nextTick(() => {
        this.$refs.editTask[0].focus()
      })
    },

    clearEditTask() {
      this.editTask = {
        id: null,
        task: null
      }
    },

    onUpdate() {
      if (!this.editTask.task.body) return
      if (this.editItem) {
        apiTo
          .updateCheckList(this.editItem.id, this.editTask.id, {
            body: this.editTask.task.body,
            completed: this.editTask.task.completed
          })
          .then(({ data }) => {
            this.clearEditTask()
            this.taskUpdated(data.result)
          })
      } else {
        this.taskUpdated(this.editTask.task)
        this.clearEditTask()
      }
    },

    taskUpdated(task) {
      const index = this.tasks.findIndex(t => t.id === task.id)
      if (~index) this.tasks.splice(index, 1, task)
    },

    onSave() {
      if (!this.newTask) return
      if (this.editItem) {
        apiTo.createCheckList(this.editItem.id, { body: this.newTask }).then(({ data }) => {
          this.newTask = ''
          this.showNewTask = false
          this.tasks.push(data.result)
          this.incrementOrDecrementChecklistCount(1)
        })
      } else {
        this.tasks.push({
          id: Symbol('id'),
          body: this.newTask,
          completed: false
        })
        this.newTask = ''
        this.showNewTask = false
      }
    },

    onDelete(task) {
      if (this.editItem) {
        apiTo.deleteCheckList(this.editItem.id, task.id).then(() => {
          this.taskDeleted(task)
          this.incrementOrDecrementChecklistCount(-1)
        })
      } else this.taskDeleted(task)
    },

    taskDeleted(task) {
      const index = this.tasks.findIndex(t => t.id === task.id)
      if (~index) this.tasks.splice(index, 1)
    },

    toggleCompletion(task) {
      if (this.editItem) {
        apiTo
          .updateCheckList(this.editItem.id, task.id, {
            body: task.body,
            completed: !task.completed
          })
          .then(({ data }) => this.taskUpdated(data.result))
      } else this.taskUpdated({ ...task, completed: !task.completed })
    },

    incrementOrDecrementChecklistCount(increment) {
      if (this.fromPage === 'kanban') {
        this.$store.commit('kanban-board/incrementOrDecrementCheckListCount', {
          todoId: this.editItem.id,
          increment
        })
      }
    },

    getPayload() {
      // access from parent
      return this.tasks.map(t => ({ body: t.body }))
    }
  }
}
</script>

<style lang="stylus" scoped>
.checklist {
  > .header {
    padding 0
    display grid
    align-items center
    grid-auto-flow column
    justify-content space-between
    margin 5px 0
    width 100%

    > .left {
      display flex
      align-items center
    }
  }

  > .tasks {
    display grid
    gap 10px
    margin-top 10px

    .task:not(.editable) {
      display grid
      grid-auto-flow column
      justify-content space-between
      gap 10px
      padding 15px
      background #eeeeee
      border-radius 5px
    }

    .spinner-loader, .no-tasks {
      padding 15px
      display grid
      justify-content center
    }
  }
}
</style>
